import React, { useEffect, useRef, useMemo } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Chart } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';
import classes from '../../bidAnalysisTemplate.module.scss';
import { getMarketTrendsChartData, type DateRange, type MarketSalesTrendItem } from '../../bidAnalysisTemplate.logic';

interface MarketTrendsChartProps {
  marketSalesTrendData: MarketSalesTrendItem[];
  filteredMarketSalesTrendData: MarketSalesTrendItem[];
  chartDateRange: DateRange;
  tempDateRange: DateRange;
  calendarViewDates: { startViewDate: Date | null; endViewDate: Date | null };
  selectedCompany: string | null;
  isLoading: boolean;
  onDateRangeChange: (startDate: Date | null, endDate: Date | null) => void;
  onApplyFilter: () => void;
  onCalendarViewDateChange: (dates: { startViewDate: Date | null; endViewDate: Date | null }) => void;
  chartOptions: ChartOptions;
  onChartModalOpen?: () => void;
}

export const MarketTrendsChart: React.FC<MarketTrendsChartProps> = ({
  marketSalesTrendData,
  filteredMarketSalesTrendData,
  chartDateRange,
  tempDateRange,
  calendarViewDates,
  selectedCompany,
  isLoading,
  onDateRangeChange,
  onApplyFilter,
  onCalendarViewDateChange,
  chartOptions,
  onChartModalOpen,
}) => {
  // Preserve last known good data to prevent chart from disappearing
  const lastMarketSalesTrendDataRef = useRef<MarketSalesTrendItem[]>([]);
  const lastFilteredDataRef = useRef<MarketSalesTrendItem[]>([]);
  
  // Preserve market sales trend data
  const preservedMarketSalesTrendData = useMemo(() => {
    if (marketSalesTrendData && marketSalesTrendData.length > 0) {
      lastMarketSalesTrendDataRef.current = marketSalesTrendData;
      return marketSalesTrendData;
    }
    if (lastMarketSalesTrendDataRef.current && lastMarketSalesTrendDataRef.current.length > 0) {
      return lastMarketSalesTrendDataRef.current;
    }
    return [];
  }, [marketSalesTrendData]);
  
  // Preserve filtered data
  const preservedFilteredData = useMemo(() => {
    if (filteredMarketSalesTrendData && filteredMarketSalesTrendData.length > 0) {
      lastFilteredDataRef.current = filteredMarketSalesTrendData;
      return filteredMarketSalesTrendData;
    }
    if (lastFilteredDataRef.current && lastFilteredDataRef.current.length > 0) {
      return lastFilteredDataRef.current;
    }
    return [];
  }, [filteredMarketSalesTrendData]);
  
  const marketTrendsData = getMarketTrendsChartData(
    preservedMarketSalesTrendData,
    preservedFilteredData,
    chartDateRange
  );

  const handleStartDateChange = (e: any) => {
    const selectedDate = e.value as Date;
    if (selectedDate) {
      onDateRangeChange(selectedDate, tempDateRange.endDate);
      onCalendarViewDateChange({
        ...calendarViewDates,
        startViewDate: selectedDate,
      });
    } else {
      onDateRangeChange(null, tempDateRange.endDate);
      onCalendarViewDateChange({
        ...calendarViewDates,
        startViewDate: null,
      });
    }
  };

  const handleStartDateViewChange = (e: any) => {
    const viewDate = e.value as Date;
    if (viewDate) {
      onDateRangeChange(viewDate, tempDateRange.endDate);
      onCalendarViewDateChange({
        ...calendarViewDates,
        startViewDate: viewDate,
      });
    }
  };

  const handleEndDateChange = (e: any) => {
    const selectedDate = e.value as Date;
    if (selectedDate) {
      onDateRangeChange(tempDateRange.startDate, selectedDate);
      onCalendarViewDateChange({
        ...calendarViewDates,
        endViewDate: selectedDate,
      });
    } else {
      onDateRangeChange(tempDateRange.startDate, null);
      onCalendarViewDateChange({
        ...calendarViewDates,
        endViewDate: null,
      });
    }
  };

  const handleEndDateViewChange = (e: any) => {
    const viewDate = e.value as Date;
    if (viewDate) {
      onDateRangeChange(tempDateRange.startDate, viewDate);
      onCalendarViewDateChange({
        ...calendarViewDates,
        endViewDate: viewDate,
      });
    }
  };

  // Prevent calendar trigger buttons from causing page scroll
  useEffect(() => {
    // Only set button type to prevent form submission - don't prevent the click event
    // This allows PrimeReact to handle the calendar opening normally
    const handleCalendarButtons = () => {
      const calendarTriggers = document.querySelectorAll('.chart-date-picker .p-calendar-trigger button');
      calendarTriggers.forEach((button) => {
        // Ensure button type is set to prevent form submission (this is the key fix)
        if (button instanceof HTMLButtonElement) {
          button.type = 'button';
        } else {
          button.setAttribute('type', 'button');
        }
      });
    };

    // Run immediately
    handleCalendarButtons();
    
    // Use MutationObserver to catch dynamically added calendar buttons
    const observer = new MutationObserver(() => {
      setTimeout(handleCalendarButtons, 0);
    });
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      observer.disconnect();
    };
  }, [selectedCompany]); // Re-run when selectedCompany changes to catch new calendar instances

  return (
    <div className={classes.chartBox}>
      <div className={classes.chartBoxHeader}>
        <h4>
          Market Sales Trends by Agency
          {selectedCompany && <span className={classes.companyName}> - {selectedCompany}</span>}
        </h4>
        {onChartModalOpen && (
          <i
            className="pi pi-external-link"
            style={{ cursor: 'pointer' }}
            onClick={onChartModalOpen}
            title="Open chart in full screen"
          ></i>
        )}
      </div>
      <div className={classes.chartControls}>
        <div className={classes.dateRangePicker}>
          <label style={{ fontSize: '0.75rem', marginRight: '0.5rem' }}>Date Range:</label>
          <Calendar
            key={`start-date-${selectedCompany || 'all'}`}
            value={tempDateRange.startDate}
            viewDate={calendarViewDates.startViewDate || tempDateRange.startDate || undefined}
            onChange={handleStartDateChange}
            onViewDateChange={handleStartDateViewChange}
            placeholder="Start Date"
            dateFormat="mm/dd/yy"
            yearNavigator
            monthNavigator
            yearRange="2010:2030"
            showIcon
            icon="pi pi-calendar"
            appendTo={typeof document !== 'undefined' ? document.body : undefined}
            style={{ width: '140px', fontSize: '0.75rem' }}
            showOnFocus={false}
            className="chart-date-picker"
          />
          <span style={{ margin: '0 0.5rem' }}>-</span>
          <Calendar
            key={`end-date-${selectedCompany || 'all'}`}
            value={tempDateRange.endDate}
            viewDate={calendarViewDates.endViewDate || tempDateRange.endDate || undefined}
            onChange={handleEndDateChange}
            onViewDateChange={handleEndDateViewChange}
            placeholder="End Date"
            dateFormat="mm/dd/yy"
            yearNavigator
            monthNavigator
            yearRange="2010:2030"
            showIcon
            icon="pi pi-calendar"
            appendTo={typeof document !== 'undefined' ? document.body : undefined}
            style={{ width: '140px', fontSize: '0.75rem' }}
            showOnFocus={false}
            className="chart-date-picker"
          />
            <Button
            label="Apply Filter"
            icon="pi pi-filter"
            onClick={onApplyFilter}
            disabled={isLoading}
            style={{ 
              marginLeft: '0.5rem', 
              padding: '0.625rem 0.75rem', 
              fontSize: '0.75rem',
              height: '2.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              color: '#374151'
            }}
            />
        </div>
      </div>
      <div style={{ height: '480px', width: '100%', position: 'relative' }}>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#522d6e' }}>
            <div style={{ textAlign: 'center' }}>
              <i className="pi pi-spinner pi-spin" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}></i>
              <p>Loading market sales trend data{selectedCompany ? ` for ${selectedCompany}` : ''}...</p>
            </div>
          </div>
        ) : (preservedFilteredData.length > 0 || preservedMarketSalesTrendData.length > 0) ? (
          <Chart type="bar" data={marketTrendsData as any} options={chartOptions} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280' }}>
            <div style={{ textAlign: 'center' }}>
              <i className="pi pi-chart-line" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}></i>
              <p>No market sales trend data available. Please validate NDC to load chart data.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

